import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { urlMapper } from '@shared/models';
import * as fromRootStore from '@store/index';
import { LogInfo } from '@store/index';
import {
  selectAuthCheckCompleted,
  selectIsAuthenticated,
} from '@store/selectors/auth/auth.selectors';
import { selectClientIncapacitated } from '@store/selectors/personal-data/personal-data.selectors';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { CookieService } from 'ngx-cookie-service';
import {
  catchError,
  combineLatest,
  filter,
  Observable,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';

@Injectable()
export class IncapacitatedGuard {
  constructor(
    private readonly cookieService: CookieService,
    private readonly router: Router,
    private readonly globalStore: Store<fromRootStore.State>,
  ) {}

  canActivate(): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }

    // Wait for authentication check to complete before proceeding
    return this.globalStore.select(selectAuthCheckCompleted).pipe(
      withLatestFrom(this.globalStore.select(selectIsAuthenticated)),
      filter(([completed, _isAuthenticated]) => completed),
      take(1),
      switchMap(([_completed, isAuthenticated]) => {
        // If not authenticated, bail out early without making API calls
        if (!isAuthenticated) {
          return of(false);
        }

        // Personal data is guaranteed to be loaded by ClientDataGuard
        return combineLatest([
          this.globalStore.select(selectClientIncapacitated),
          this.globalStore.select(flowTypeSelection),
        ]).pipe(
          take(1),
          switchMap(([incapacitated, flowType]) => {
            if (incapacitated) {
              this.globalStore.dispatch(
                LogInfo({
                  payload: {
                    eventId: 'ineligible-page-route-incapacitatedGuard',
                    message: `Client has no access to the ${flowType} flow`,
                  },
                }),
              );
              this.router.navigate([`${urlMapper[flowType]}/ineligible`]);
              return of(false);
            }
            return of(true);
          }),
          catchError(() => of(false)),
        );
      }),
      catchError(() => of(false)),
    );
  }
}
